import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {{ id: number, title: string, options: string[], price: number, available: boolean, tracked: boolean, stock: number, image: string | null }} AddOnVariant
 * @typedef {{ id: number, title: string, url: string, image: string | null, variants: AddOnVariant[] }} AddOnProduct
 * @typedef {{ mode: 'addons' | 'parent', currency: string, moneyFormat: string, max: number, current: AddOnProduct, products: AddOnProduct[] }} AddOnsData
 */

/**
 * « Options à ajouter »: a main product (e.g. a bracelet) bought together with
 * optional products (e.g. charms), added as nested cart lines under it.
 *
 * - `addons` mode, on the main product: each option card has its own option
 *   chips and a button that puts the chosen variant in the selection. One
 *   button then adds the main product (the variant picked in the native
 *   picker) and the selection. The native buy buttons hide while the
 *   selection is not empty.
 * - `parent` mode, on an option product: the customer picks a main product
 *   variant in one of the cards, and one button adds it with the current
 *   product (the variant picked in the native picker).
 *
 * The panel starts closed on one line that shows the starting price, then
 * what the customer picked. With several linked products, a switch shows one
 * card at a time.
 *
 * Choices that would need more than the stock left (stock minus what is in
 * the cart and in the selection) are flagged and cannot be added. Shopify
 * still checks the stock on add and at checkout.
 */
class PyAddOns extends HTMLElement {
  /** @type {AddOnsData | null} */
  #data = null;

  /** @type {Map<number, number>} variant id → quantity already in the cart */
  #inCart = new Map();

  /** @type {Map<number, number>} variant id → quantity picked (addons mode) */
  #picked = new Map();

  /** @type {number | null} product id of the card last used (parent mode) */
  #activeProduct = null;

  /** @type {number | null} product id of the card shown by the switch */
  #shownProduct = null;

  /** @type {HTMLDetailsElement | null} */
  #disclosure = null;

  /** Set on the first choice, so the closed panel and the button only reflect what the customer picked. */
  #touched = false;

  #busy = false;

  connectedCallback() {
    const json = this.querySelector('script[data-py-addons-data]');
    if (!json?.textContent) return;

    try {
      this.#data = JSON.parse(json.textContent);
    } catch (error) {
      console.error('[py-add-ons] Invalid data', error);
      return;
    }

    this.#preselectSingleValues();
    this.#shownProduct = this.#data.products[0]?.id ?? null;
    if (this.#data.mode === 'parent') this.#activeProduct = this.#shownProduct;

    const disclosure = this.querySelector('details[data-disclosure]');
    this.#disclosure = disclosure instanceof HTMLDetailsElement ? disclosure : null;
    this.#disclosure?.addEventListener('toggle', this.#onToggle);

    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#section()?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);

    this.#render();
    this.#syncCart();
  }

  disconnectedCallback() {
    this.#disclosure?.removeEventListener('toggle', this.#onToggle);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#section()?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  #section() {
    return this.closest('.shopify-section');
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.hasAttribute('data-switch')) {
      this.#shownProduct = Number(target.value);
      if (this.#data?.mode === 'parent') this.#activeProduct = this.#shownProduct;
    } else {
      const card = target instanceof Element ? target.closest('[data-product]') : null;
      if (card instanceof HTMLElement) this.#activeProduct = Number(card.dataset.product);
    }
    this.#touched = true;
    this.#setMessage('');
    this.#render();
  };

  #onToggle = () => this.#render();

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const pick = event.target.closest('[data-pick]');
    if (pick) {
      const card = pick.closest('[data-product]');
      if (card instanceof HTMLElement) this.#pick(card);
      return;
    }

    const remove = event.target.closest('[data-remove]');
    if (remove) {
      const item = remove.closest('[data-variant]');
      if (item instanceof HTMLElement) this.#unpick(Number(item.dataset.variant));
      return;
    }

    if (event.target.closest('[data-add]')) this.#onAdd();
  };

  /** Keeps the stock left in step when the cart changes elsewhere (drawer, other forms). */
  #onCartLinesUpdate = (/** @type {CartLinesUpdateEvent} */ event) => {
    if (event.target === this) return;
    event.promise?.then(() => this.#syncCart()).catch(() => {});
  };

  /** The native picker changed variant: refresh the total once the form has the new id. */
  #onProductSelect = (/** @type {Event & { promise?: Promise<unknown> }} */ event) => {
    event.promise?.then(() => setTimeout(() => this.#render())).catch(() => {});
  };

  /** Options with a single value (e.g. one size) are picked for the customer. */
  #preselectSingleValues() {
    for (const fieldset of this.querySelectorAll('[data-option]')) {
      const inputs = fieldset.querySelectorAll('input[type="radio"]');
      if (inputs.length === 1 && inputs[0] instanceof HTMLInputElement) inputs[0].checked = true;
    }
  }

  /** @param {number} id */
  #product(id) {
    return this.#data?.products.find((product) => product.id === id) ?? null;
  }

  /** @param {number | null} id */
  #variant(id) {
    if (id === null || !this.#data) return null;
    for (const product of [this.#data.current, ...this.#data.products]) {
      const variant = product.variants.find((item) => item.id === id);
      if (variant) return { product, variant };
    }
    return null;
  }

  /** The variant picked in the native picker of this product page. */
  #currentVariant() {
    if (!this.#data) return null;
    const input = this.#section()?.querySelector('product-form-component input[name="id"]');
    const id = input instanceof HTMLInputElement ? Number(input.value) : null;
    const variants = this.#data.current.variants;
    return variants.find((variant) => variant.id === id) ?? (variants.length === 1 ? variants[0] : null) ?? null;
  }

  /** @param {AddOnVariant} variant */
  #left(variant) {
    if (!variant.available) return 0;
    if (!variant.tracked) return Infinity;
    return variant.stock - (this.#inCart.get(variant.id) ?? 0) - (this.#picked.get(variant.id) ?? 0);
  }

  /** @param {HTMLElement} card */
  #selectedValues(card) {
    return Array.from(card.querySelectorAll('[data-option]'), (fieldset) => {
      const input = fieldset.querySelector('input:checked');
      return input instanceof HTMLInputElement ? input.value : null;
    });
  }

  /**
   * The variant matching the chips of a card, once every option is picked.
   * @param {HTMLElement} card
   */
  #cardVariant(card) {
    const product = this.#product(Number(card.dataset.product));
    if (!product) return null;
    if (product.variants.length === 1) return product.variants[0] ?? null;

    const values = this.#selectedValues(card);
    if (values.some((value) => value === null)) return null;
    return product.variants.find((variant) => variant.options.every((value, i) => value === values[i])) ?? null;
  }

  #pickedCount() {
    let count = 0;
    for (const quantity of this.#picked.values()) count += quantity;
    return count;
  }

  /** @param {HTMLElement} card */
  #pick(card) {
    const variant = this.#cardVariant(card);
    if (!variant || this.#left(variant) < 1) return;

    if (this.#pickedCount() >= (this.#data?.max ?? Infinity)) {
      this.#setMessage(this.dataset.maxText || '', 'error');
      return;
    }

    this.#picked.set(variant.id, (this.#picked.get(variant.id) ?? 0) + 1);
    this.#setMessage('');
    this.#render();
  }

  /** @param {number} id */
  #unpick(id) {
    const quantity = (this.#picked.get(id) ?? 0) - 1;
    if (quantity > 0) this.#picked.set(id, quantity);
    else this.#picked.delete(id);
    this.#setMessage('');
    this.#render();
  }

  #render() {
    if (!this.#data) return;

    const atMax = this.#pickedCount() >= this.#data.max;

    for (const card of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-product]'))) {
      const product = this.#product(Number(card.dataset.product));
      if (!product) continue;

      card.hidden = product.id !== this.#shownProduct;
      this.#renderChips(card, product);

      const variant = this.#cardVariant(card);
      const inStock = variant ? this.#left(variant) >= 1 : false;

      const soldOut = card.querySelector('[data-soldout]');
      if (soldOut instanceof HTMLElement) soldOut.hidden = !variant || inStock;

      const pick = card.querySelector('[data-pick]');
      if (pick instanceof HTMLButtonElement) pick.disabled = !inStock || atMax;

      const image = card.querySelector('.py-addons__product-image');
      if (image instanceof HTMLImageElement) {
        const src = variant?.image ?? product.image;
        if (src && image.dataset.src !== src) {
          image.dataset.src = src;
          image.srcset = '';
          image.src = src;
        }
      }

      const price = card.querySelector('[data-price]');
      if (price && variant) price.textContent = this.#money(variant.price);

      card.toggleAttribute('data-active', this.#data.mode === 'parent' && this.#activeProduct === product.id);
    }

    if (this.#data.mode === 'addons') this.#renderPicked();

    const current = this.#currentVariant();
    const parent = this.#parentChoice();
    const extras = this.#data.mode === 'addons' ? this.#pickedItems() : current ? [{ variant: current, quantity: 1 }] : [];

    let total = parent?.variant.price ?? 0;
    for (const { variant, quantity } of extras) total += variant.price * quantity;

    const totalElement = this.querySelector('[data-total]');
    if (totalElement) totalElement.textContent = this.#money(total);

    const ready =
      !!parent &&
      this.#left(parent.variant) >= 1 &&
      extras.length > 0 &&
      extras.every(({ variant, quantity }) => this.#left(variant) + (this.#picked.get(variant.id) ?? 0) >= quantity);

    const button = this.querySelector('[data-add]');
    if (button instanceof HTMLButtonElement) button.disabled = this.#busy || !ready;

    const chosen = this.#touched && parent ? parent : null;
    if (this.#data.mode === 'parent') {
      const summary = this.querySelector('[data-summary]');
      if (summary instanceof HTMLElement) summary.hidden = !(this.#disclosure?.open ?? true) && !chosen;
    }
    this.#renderTeaser(chosen);
  }

  /**
   * The closed panel shows the starting price, then what the customer picked.
   * @param {{ product: AddOnProduct, variant: AddOnVariant } | null} chosen - Main product picked (parent mode)
   */
  #renderTeaser(chosen) {
    const teaser = this.querySelector('[data-teaser]');
    if (!(teaser instanceof HTMLElement) || !this.#data) return;

    let text = teaser.dataset.default ?? '';
    if (this.#data.mode === 'addons') {
      const labels = this.#pickedItems().map(({ product, variant, quantity }) => {
        const label = this.#label(product, variant);
        return quantity > 1 ? `${label} × ${quantity}` : label;
      });
      if (labels.length > 0) text = labels.join(', ');
    } else if (chosen) {
      text = this.#label(chosen.product, chosen.variant);
    }
    teaser.textContent = text;
  }

  /**
   * Flags the chips whose value cannot be bought with the other values picked.
   * @param {HTMLElement} card
   * @param {AddOnProduct} product
   */
  #renderChips(card, product) {
    const values = this.#selectedValues(card);

    card.querySelectorAll('[data-option]').forEach((fieldset, index) => {
      for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
        if (!(input instanceof HTMLInputElement)) continue;

        const buyable = product.variants.some(
          (variant) =>
            variant.options[index] === input.value &&
            variant.options.every((value, i) => i === index || values[i] === null || values[i] === value) &&
            this.#left(variant) >= 1
        );

        input.parentElement?.toggleAttribute('data-soldout', !buyable);
      }
    });
  }

  /** The main product to add: the page's product (addons mode) or the card last used (parent mode). */
  #parentChoice() {
    if (!this.#data) return null;

    if (this.#data.mode === 'addons') {
      const variant = this.#currentVariant();
      return variant ? { product: this.#data.current, variant } : null;
    }

    if (this.#activeProduct === null) return null;
    const card = this.querySelector(`[data-product="${this.#activeProduct}"]`);
    const product = this.#product(this.#activeProduct);
    const variant = card instanceof HTMLElement ? this.#cardVariant(card) : null;
    return product && variant ? { product, variant } : null;
  }

  #pickedItems() {
    /** @type {Array<{ product: AddOnProduct, variant: AddOnVariant, quantity: number }>} */
    const items = [];
    for (const [id, quantity] of this.#picked) {
      const found = this.#variant(id);
      if (found) items.push({ ...found, quantity });
    }
    return items;
  }

  #renderPicked() {
    const list = this.querySelector('[data-picked]');
    const wrap = this.querySelector('[data-picked-wrap]');
    const summary = this.querySelector('[data-summary]');
    const template = this.querySelector('template[data-picked-template]');
    if (!list || !(template instanceof HTMLTemplateElement)) return;

    const items = this.#pickedItems();
    list.replaceChildren();

    for (const { product, variant, quantity } of items) {
      for (let i = 0; i < quantity; i++) {
        const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
        const item = fragment.firstElementChild;
        if (!(item instanceof HTMLElement)) continue;
        item.dataset.variant = String(variant.id);
        const name = item.querySelector('[data-name]');
        if (name) name.textContent = this.#label(product, variant);
        const price = item.querySelector('[data-price]');
        if (price) price.textContent = this.#money(variant.price);
        list.append(fragment);
      }
    }

    const hasItems = items.length > 0;
    if (wrap instanceof HTMLElement) wrap.hidden = !hasItems;
    if (summary instanceof HTMLElement) summary.hidden = !hasItems;
    this.toggleAttribute('data-active', hasItems);
  }

  /**
   * @param {AddOnProduct} product
   * @param {AddOnVariant} variant
   */
  #label(product, variant) {
    return product.variants.length === 1 ? product.title : `${product.title} (${variant.title})`;
  }

  /** @param {number} cents */
  #money(cents) {
    if (!this.#data) return '';
    return formatMoney(cents, this.#data.moneyFormat, this.#data.currency);
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /** @param {{ items?: Array<{ variant_id: number, quantity: number }> }} cart */
  #setCart(cart) {
    this.#inCart = new Map();
    for (const item of cart.items ?? []) {
      this.#inCart.set(item.variant_id, (this.#inCart.get(item.variant_id) ?? 0) + item.quantity);
    }
    this.#render();
  }

  async #syncCart() {
    try {
      this.#setCart(await this.#fetchCart());
    } catch (error) {
      console.warn('[py-add-ons]', error);
    }
  }

  async #onAdd() {
    if (!this.#data || this.#busy) return;

    const parent = this.#parentChoice();
    const current = this.#currentVariant();
    const extras =
      this.#data.mode === 'addons'
        ? this.#pickedItems()
        : current
          ? [{ product: this.#data.current, variant: current, quantity: 1 }]
          : [];
    if (!parent || extras.length === 0) return;

    // A hidden set id keeps each main product with its options on its own cart
    // line, even when the same variant is already in the cart, and ties the
    // lines together in the order.
    const set = `${Date.now()}`;
    const label = this.dataset.propertyLabel?.trim();
    const optionProperties = { ...(label ? { [label]: this.#label(parent.product, parent.variant) } : {}), _py_set: set };

    const parentLine = { id: parent.variant.id, quantity: 1, properties: { _py_set: set } };

    /** @param {string | null} parentKey */
    const optionLines = (parentKey) =>
      extras.map(({ variant, quantity }) => ({
        id: variant.id,
        quantity,
        ...(parentKey ? { parent_line_key: parentKey } : {}),
        properties: optionProperties,
      }));
    const items = [parentLine, ...optionLines(null)];

    const sectionIds = [];
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.push(element.dataset.sectionId);
    }

    this.#busy = true;
    this.#setMessage('');
    this.#render();

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    const detail = {
      source: 'py-add-ons',
      sourceId: this.id,
      itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
      productId: String(this.#data.current.id),
    };

    try {
      /**
       * @param {Array<Record<string, unknown>>} lines
       * @param {boolean} [withSections]
       */
      const add = (lines, withSections = false) =>
        fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: lines, ...(withSections ? { sections: sectionIds.join(',') } : {}) }),
        })
          .then((res) => res.json())
          .then((json) => {
            if (json.status) console.warn('[py-add-ons] add refused', lines, json);
            return json;
          });

      // The main product goes in alone first: Shopify refuses to mix a bundle
      // (e.g. a bracelet sold through Shopify Bundles) with other lines in a
      // request that nests them. A refused add adds nothing, so retrying is safe.
      let response = await add([parentLine]);
      if (response.status) response = await add([{ id: parent.variant.id, quantity: 1 }]);

      if (!response.status) {
        const parentKey = response.items?.[0]?.key ?? null;
        // Nested under the main product when Shopify allows it (not under a
        // bundle), else plain lines named by the visible property.
        let options = parentKey ? await add(optionLines(parentKey), true) : { status: 422 };
        if (options.status) options = await add(optionLines(null), true);
        response = options;
      }

      const cart = await this.#fetchCart();

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, items: cart.items, didError: true },
        });
        this.#setMessage(response.description || this.dataset.errorText || '', 'error');
      } else {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, items: cart.items, sections: response.sections, didError: false },
        });
        this.#picked.clear();
        this.#setMessage(this.dataset.addedText || '');
      }

      this.#busy = false;
      this.#setCart(cart);
    } catch (error) {
      console.error('[py-add-ons]', error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#busy = false;
      this.#setMessage(this.dataset.errorText || '', 'error');
      this.#render();
    }
  }

  /**
   * @param {string} text
   * @param {'error'} [state]
   */
  #setMessage(text, state) {
    const message = /** @type {HTMLElement | null} */ (this.querySelector('[data-message]'));
    if (!message) return;
    message.textContent = text;
    if (state) message.dataset.state = state;
    else delete message.dataset.state;
  }
}

if (!customElements.get('py-add-ons')) {
  customElements.define('py-add-ons', PyAddOns);
}
